/* eslint-disable no-shadow */

import Vue from 'vue';
import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { ValidationError } from '@/errors';

const endpoint = `${apiEndpoint}/users`;

const state = {
  users: null,
  pending: false,
  error: null,
  updatePending: false,
  updateError: null,
};

const getters = {
  loaded: (state) => !state.pending && state.users != null,
  users: (state, getters) => (getters.loaded ? state.users : []),
  noUsers: (state, getters) => getters.loaded && !getters.users.length,
};

const mutations = {
  clearErrors(state) {
    state.error = null;
    state.updateError = null;
  },
  beforeLoading(state, initial = false) {
    if (initial) {
      state.users = null;
    }
    state.error = null;
    state.pending = true;
  },
  beforeUpdate(state) {
    state.updateError = null;
    state.updatePending = true;
  },
  error(state, error) {
    state.pending = false;
    state.error = error;
  },
  data(state, { users }) {
    state.pending = false;
    state.users = users;
  },
  update(state, error = null) {
    state.updatePending = false;
    state.updateError = error;
  },
  created(state, user) {
    state.users.push(JSON.parse(JSON.stringify(user)));
    state.updatePending = false;
    state.updateError = null;
  },
  modified(state, user) {
    const idx = state.users.findIndex((p) => p.id === user.id);
    if (idx !== -1) {
      Vue.set(state.users, idx, JSON.parse(JSON.stringify(user)));
    }
    state.updatePending = false;
    state.updateError = null;
  },
  deleted(state, pid) {
    const idx = state.users.findIndex((user) => user.id === pid);
    if (idx !== -1) {
      state.users.splice(idx, 1);
    }
    state.updatePending = false;
    state.updateError = null;
  },
};

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 422:
      err = new ValidationError(status, data.message, data.errors);
      break;
    default:
      break;
  }
  commit('update', err);
  throw err;
}

const actions = {
  async fetchData({ commit }, forced = false) {
    commit('beforeLoading', forced);
    try {
      const result = await ApiService.get(`${endpoint}`);
      const users = result.data.data;
      commit('data', { users });
    } catch (error) {
      commit('error', error);
    }
  },
  async createUser({ commit }, user) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.post(`${endpoint}`, { ...user });
      commit('created', data);
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async updateUser({ commit }, user) {
    commit('beforeUpdate');
    try {
      await ApiService.put(`${endpoint}/${user.id}`, { ...user });
      commit('modified', { ...user });
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async deleteUser({ commit }, pid) {
    commit('beforeUpdate');
    try {
      await ApiService.delete(`${endpoint}/${pid}`);
      commit('deleted', pid);
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
